/**
 * The moon-gate walk: a scholar's-garden connector. Plain whitewashed walls and
 * a marble floor, and around every doorway a thick stone ring standing proud of
 * the plaster — the moon gate of a Chinese garden court, here reduced to relief
 * because the opening itself belongs to the generator. A long stone trough with
 * three scholar's rocks runs down one wall, and a single slot of light runs the
 * length of the ceiling.
 *
 * The rings are pure decoration: they hug the wall face, never reach into the
 * door's clear rectangle, and carry no colliders.
 */

import { PALACE } from '../constants';
import { rectSegments } from '../hang';

const RING_RI = 2.3; // inner radius — just clears a standard door's corners
const RING_T = 0.34; // ring thickness
const RING_PROUD = 0.13; // how far the ring stands off the wall face
const RING_BITE = 0.05; // how far it buries into the wall (no coplanar faces)
const RING_BANDS = 20; // horizontal courses used to approximate the circle
const JAMB_CLEAR = 0.22; // kept between the door reveal (plus casing) and the ring

const PLANTER_H = 0.5;
const PLANTER_D = 0.72;
const PLANTER_MAX = 6.5;

const interiorOf = (node) => ({
  x1: node.x1 + PALACE.WALL_T,
  x2: node.x2 - PALACE.WALL_T,
  z1: node.z1 + PALACE.WALL_T,
  z2: node.z2 - PALACE.WALL_T,
});

const isHorizontal = (side) => side === 'N' || side === 'S';

const faceOf = (node, side) => {
  const I = interiorOf(node);
  if (side === 'N') return { face: I.z2, inward: -1 };
  if (side === 'S') return { face: I.z1, inward: 1 };
  if (side === 'E') return { face: I.x2, inward: -1 };
  return { face: I.x1, inward: 1 };
};

// The strip of floor each door needs in front of it. A solid hugging one wall
// can still reach round a corner into another door's approach, so the planter
// is tested against all four.
const doorCorridors = (node, pad, depth = 3.2) =>
  (node.doors || []).map((d) => {
    const half = (d.w || PALACE.DOOR_W) / 2 + pad(d);
    if (d.side === 'N') return [d.at - half, node.z2 - depth, d.at + half, node.z2];
    if (d.side === 'S') return [d.at - half, node.z1, d.at + half, node.z1 + depth];
    if (d.side === 'E') return [node.x2 - depth, d.at - half, node.x2, d.at + half];
    return [node.x1, d.at - half, node.x1 + depth, d.at + half];
  });

// Runs along a wall where a solid `dep` deep clears every door and every moon
// ring, in world along-coordinates.
const clearRuns = (node, side, dep, samePad, otherPad) => {
  const I = interiorOf(node);
  const horizontal = isHorizontal(side);
  const { face, inward } = faceOf(node, side);
  const c1 = Math.min(face, face + inward * dep);
  const c2 = Math.max(face, face + inward * dep);
  const lo = (horizontal ? I.x1 : I.z1) + 0.4;
  const hi = (horizontal ? I.x2 : I.z2) - 0.4;

  const blocks = doorCorridors(node, (d) => (d.side === side ? samePad : otherPad))
    .filter((cor) => (horizontal ? cor[1] < c2 && c1 < cor[3] : cor[0] < c2 && c1 < cor[2]))
    .map((cor) => (horizontal ? [cor[0], cor[2]] : [cor[1], cor[3]]))
    .sort((a, b) => a[0] - b[0]);

  const runs = [];
  let cursor = lo;
  for (const [a, b] of blocks) {
    if (a - cursor > 0.2) runs.push([cursor, Math.min(a, hi)]);
    cursor = Math.max(cursor, b);
  }
  if (hi - cursor > 0.2) runs.push([cursor, hi]);
  return runs.filter(([a, b]) => b - a > 0.2);
};

const moonGate = {
  id: 'moonGate',
  role: 'route',
  weight: 2,

  plan(rand, { axis }) {
    const long = 11 + rand() * 3;
    const cross = 6.5 + rand() * 1.5;
    return {
      w: axis === 'x' ? long : cross,
      d: axis === 'x' ? cross : long,
      height: 7,
    };
  },

  // Hung on the plaster between the moon rings, which is why the door pad is so
  // wide: it has to clear the ring's whole outer radius, not just the opening.
  segments(node) {
    return rectSegments(node, {
      longStyle: 'row',
      shortStyle: 'hero',
      doorPad: RING_RI + RING_T - PALACE.DOOR_W / 2 + 0.35,
    });
  },

  build(node, T) {
    const I = interiorOf(node);
    const longX = I.x2 - I.x1 >= I.z2 - I.z1;
    const layLong = Math.max(2, (longX ? I.x2 - I.x1 : I.z2 - I.z1) - 2.4);
    const layCross = 2.4;
    const { group, colliders } = T.standardShell(node, T.kit, {
      wallMaterial: T.kit.wallFor(node.palette || 0, true),
      floorMaterial: T.kit.materials.marble,
      laylight: { w: longX ? layLong : layCross, d: longX ? layCross : layLong },
    });

    const stone = [];

    // --- a moon-gate ring around every door, stacked out of horizontal
    // courses. Each course is the chord of the annulus at that height; courses
    // that would fall inside the door's clear rectangle are pushed out past
    // the casing, so the opening the generator cut stays exactly as cut.
    const ro = RING_RI + RING_T;
    for (const door of node.doors || []) {
      const dw = door.w || PALACE.DOOR_W;
      const dh = door.h || PALACE.DOOR_H;
      const { face, inward } = faceOf(node, door.side);
      const horizontal = isHorizontal(door.side);
      const cy = dh / 2;
      const p1 = face - inward * RING_BITE;
      const p2 = face + inward * RING_PROUD;
      // Never let a course run past the corner of its own wall.
      const lo = (horizontal ? I.x1 : I.z1) - RING_BITE;
      const hi = (horizontal ? I.x2 : I.z2) + RING_BITE;

      const step = (2 * ro) / RING_BANDS;
      for (let i = 0; i < RING_BANDS; i++) {
        const y0 = Math.max(-0.1, cy - ro + i * step);
        const y1 = cy - ro + (i + 1) * step;
        if (y1 - y0 < 0.02) continue;
        const dy = Math.abs((y0 + y1) / 2 - cy);
        if (dy >= ro) continue;
        const xo = Math.sqrt(ro * ro - dy * dy);
        let xi = dy < RING_RI ? Math.sqrt(RING_RI * RING_RI - dy * dy) : 0;
        // Inside the door's own height band the ring must stay clear of the
        // reveal and its architrave.
        if (y0 < dh && y1 > 0) xi = Math.max(xi, dw / 2 + JAMB_CLEAR);
        if (xo - xi < 0.04) continue;

        const course = (a1, a2) => {
          const c1 = Math.max(lo, a1);
          const c2 = Math.min(hi, a2);
          if (c2 - c1 < 0.04) return;
          stone.push(
            horizontal ? T.box(c1, c2, y0, y1, p1, p2) : T.box(p1, p2, y0, y1, c1, c2)
          );
        };
        if (xi === 0) {
          course(door.at - xo, door.at + xo);
        } else {
          course(door.at - xo, door.at - xi);
          course(door.at + xi, door.at + xo);
        }
      }
    }

    // --- a long stone trough with scholar's rocks, on the clearest long wall
    const longSides = longX ? ['N', 'S'] : ['E', 'W'];
    let best = null;
    for (const side of longSides) {
      // On its own wall the planter must also clear the moon ring's footprint.
      for (const [r1, r2] of clearRuns(node, side, PLANTER_D, ro + 0.5 - PALACE.DOOR_W / 2, 0.55)) {
        const len = Math.min(PLANTER_MAX, r2 - r1 - 0.4);
        if (len >= 2.5 && (!best || len > best.len)) {
          best = { side, at: (r1 + r2) / 2, len };
        }
      }
    }
    if (best) {
      const { face, inward } = faceOf(node, best.side);
      const horizontal = isHorizontal(best.side);
      const p1 = face - inward * 0.04; // bite into the wall, no shared face
      const p2 = face + inward * PLANTER_D;
      const a1 = best.at - best.len / 2;
      const a2 = best.at + best.len / 2;
      stone.push(
        horizontal ? T.box(a1, a2, 0, PLANTER_H, p1, p2) : T.box(p1, p2, 0, PLANTER_H, a1, a2)
      );
      const rect = horizontal
        ? [a1, Math.min(p1, p2), a2, Math.max(p1, p2)]
        : [Math.min(p1, p2), a1, Math.max(p1, p2), a2];
      colliders.push({ rect, top: 2.2 });

      // Three scholar's rocks standing in the trough: upright, faceted, each
      // rooted below the rim so none of them floats.
      const pc = face + inward * (PLANTER_D / 2);
      for (let i = 0; i < 3; i++) {
        const t = (i + 1) / 4;
        const a = a1 + (a2 - a1) * t + (T.rand() - 0.5) * 0.4;
        const r = 0.3 + T.rand() * 0.14;
        const tall = 1.3 + T.rand() * 0.7;
        // Coarse sphere, not an icosahedron: it stays indexed, so it merges
        // with the boxes, and the low segment count reads as split rock. Equal
        // x/z scale keeps it inside the trough whatever the spin.
        const rock = new T.THREE.SphereGeometry(r, 5, 3);
        rock.scale(0.78, tall, 0.78);
        rock.rotateY(T.rand() * Math.PI);
        rock.translate(horizontal ? a : pc, PLANTER_H + r * tall * 0.62, horizontal ? pc : a);
        stone.push(rock);
      }
    }

    if (stone.length) group.add(T.mergedMesh(stone, T.kit.materials.stone));
    return { group, colliders };
  },
};

export default moonGate;
